import { useEffect, useRef, useState } from "react";

const crystals = [
  {
    name: "紫水晶",
    subtitle: "智慧與靈性提升",
    desc: "象徵智慧、靈性與直覺，有助於提升思維清晰度，帶來平靜與內在平衡。",
    color: "#9b8ec4",
    bg: "#f3f0f9",
    img: "https://readdy.ai/api/search-image?query=amethyst%20purple%20crystal%20cluster%20natural%20gemstone%20on%20white%20marble%20surface%20luxury%20jewelry%20product%20photography%20soft%20purple%20light%20bokeh%20background%20elegant%20minimalist%20style%20high%20detail%20texture&width=400&height=400&seq=crystal001&orientation=squarish",
  },
  {
    name: "白水晶",
    subtitle: "淨化與平衡能量",
    desc: "被稱為「能量之王」，具有淨化、放大與平衡能量的特質，是最萬用的水晶之一。",
    color: "#a8b5c0",
    bg: "#f0f4f7",
    img: "https://readdy.ai/api/search-image?query=clear%20quartz%20crystal%20point%20natural%20gemstone%20on%20white%20marble%20surface%20luxury%20jewelry%20product%20photography%20soft%20white%20light%20bokeh%20background%20elegant%20minimalist%20style%20high%20detail%20transparent%20texture&width=400&height=400&seq=crystal002&orientation=squarish",
  },
  {
    name: "黑曜石",
    subtitle: "守護與能量防護",
    desc: "強力的守護石，能夠吸收並轉化負能量，為佩戴者建立能量防護屏障。",
    color: "#4a4a4a",
    bg: "#f0f0f0",
    img: "https://readdy.ai/api/search-image?query=black%20obsidian%20sphere%20natural%20gemstone%20on%20white%20marble%20surface%20luxury%20jewelry%20product%20photography%20soft%20neutral%20light%20bokeh%20background%20elegant%20minimalist%20style%20high%20detail%20glossy%20texture&width=400&height=400&seq=crystal003&orientation=squarish",
  },
  {
    name: "金髮晶",
    subtitle: "財富與事業運",
    desc: "內含金色髮絲般的金紅石，象徵財富、事業與正能量，是招財旺運的首選水晶。",
    color: "#c9a96e",
    bg: "#faf5ec",
    img: "https://readdy.ai/api/search-image?query=golden%20rutilated%20quartz%20crystal%20natural%20gemstone%20with%20golden%20needle%20inclusions%20on%20white%20marble%20surface%20luxury%20jewelry%20product%20photography%20warm%20golden%20light%20bokeh%20background%20elegant%20minimalist%20style%20high%20detail&width=400&height=400&seq=crystal004&orientation=squarish",
  },
];

export default function Crystals() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 md:py-36 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div
          className={`mb-16 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-px bg-[#8b6f47]"></span>
            <span
              className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              NATURAL CRYSTALS
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <h2
              className="text-[#2d2520] font-bold leading-[1.4]"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              天然礦石與水晶能量
            </h2>
            <p
              className="text-[#7a6a5a] max-w-md leading-[1.75]"
              style={{
                fontSize: "1.1875rem",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              大自然孕育的礦石與水晶蘊含著獨特能量與美麗紋理，不同水晶象徵不同的祝福與能量陪伴。
            </p>
          </div>
        </div>

        {/* Crystal Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {crystals.map((item, i) => (
            <div
              key={item.name}
              className={`rounded-2xl overflow-hidden transition-all duration-1000 cursor-pointer hover:-translate-y-1 group`}
              style={{
                backgroundColor: item.bg,
                transitionDelay: `${i * 120}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(24px)",
              }}
            >
              {/* Image */}
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              {/* Content */}
              <div className="p-6">
                <div
                  className="w-8 h-1 rounded-full mb-4"
                  style={{ backgroundColor: item.color }}
                ></div>
                <h3
                  className="text-[#2d2520] font-bold mb-1"
                  style={{
                    fontSize: "clamp(1.25rem, 2vw, 1.625rem)",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {item.name}
                </h3>
                <p
                  className="font-medium mb-3"
                  style={{
                    color: item.color,
                    fontSize: "1rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {item.subtitle}
                </p>
                <p
                  className="text-[#7a6a5a] leading-[1.75]"
                  style={{
                    fontSize: "1.1875rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
